import { supabase, isSupabaseAvailable } from './supabaseClient';
import { User } from '@supabase/supabase-js';

// ============================================
// BASE SERVICE - Generic CRUD Operations
// ============================================

export type TableName = 'patients' | 'test_results' | 'evolutivos' | 'clinicians' | 'usage_tracking' | 'consent_records' | 'audit_log';

/**
 * Get the current authenticated user
 * Returns null if not authenticated or Supabase unavailable
 */
export const getCurrentUser = async (): Promise<User | null> => {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return null;
  }

  try {
    const { data: { user } } = await supabase!.auth.getUser();
    return user;
  } catch (err) {
    console.error('Failed to get current user:', err);
    return null;
  }
};

/**
 * Require authenticated user - throws if not authenticated
 */
export const requireUser = async (): Promise<User> => {
  const user = await getCurrentUser();
  if (!user) {
    throw new Error('No authenticated user');
  }
  return user;
};

/**
 * Generic fetch all records from a table
 * SECURITY: Filters by user_id (defense-in-depth, RLS also enforces this)
 */
export async function fetchAll<TDb, TEntity>(
  table: TableName,
  mapper: (row: TDb) => TEntity,
  options?: {
    orderBy?: string;
    ascending?: boolean;
    filters?: Array<{ column: string; value: unknown; operator?: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' }>;
    userIdColumn?: string;
  }
): Promise<TEntity[]> {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return [];
  }

  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn(`No authenticated user, cannot fetch ${table}`);
      return [];
    }

    const userIdColumn = options?.userIdColumn ?? 'user_id';
    let query = supabase!.from(table).select('*').eq(userIdColumn, user.id);

    // Apply additional filters
    if (options?.filters) {
      for (const filter of options.filters) {
        const operator = filter.operator ?? 'eq';
        query = query[operator](filter.column, filter.value);
      }
    }

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy, { ascending: options.ascending ?? true });
    }

    const { data, error } = await query;

    if (error) {
      console.error(`Error fetching ${table}:`, error);
      return [];
    }

    return (data || []).map(row => mapper(row as TDb));
  } catch (err) {
    console.error(`Failed to fetch ${table}:`, err);
    return [];
  }
}

/**
 * Generic fetch single record by ID
 * SECURITY: Filters by user_id (defense-in-depth, RLS also enforces this)
 */
export async function fetchById<TDb, TEntity>(
  table: TableName,
  id: string,
  mapper: (row: TDb) => TEntity,
  options?: {
    userIdColumn?: string;
    skipUserFilter?: boolean;
  }
): Promise<TEntity | null> {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return null;
  }

  try {
    const userIdColumn = options?.userIdColumn ?? 'user_id';
    let query = supabase!.from(table).select('*').eq('id', id);

    // Add user filter unless explicitly skipped
    if (!options?.skipUserFilter) {
      const user = await getCurrentUser();
      if (!user) {
        console.warn(`No authenticated user, cannot fetch ${table}`);
        return null;
      }
      query = query.eq(userIdColumn, user.id);
    }

    const { data, error } = await query.single();

    if (error) {
      console.error(`Error fetching ${table} by id:`, error);
      return null;
    }

    return data ? mapper(data as TDb) : null;
  } catch (err) {
    console.error(`Failed to fetch ${table} by id:`, err);
    return null;
  }
}

/**
 * Generic create record
 * SECURITY: Explicitly sets user_id from authenticated user
 */
export async function createRecord<TDb, TEntity, TInput>(
  table: TableName,
  data: TInput,
  toDb: (input: TInput & { userId: string }) => Record<string, unknown>,
  mapper: (row: TDb) => TEntity
): Promise<TEntity | null> {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return null;
  }

  try {
    const user = await requireUser();

    const dataWithUser = { ...data, userId: user.id } as TInput & { userId: string };
    const dbData = toDb(dataWithUser);

    const { data: result, error } = await supabase!
      .from(table)
      .insert([dbData])
      .select()
      .single();

    if (error) {
      console.error(`Error creating ${table}:`, error);
      return null;
    }

    return result ? mapper(result as TDb) : null;
  } catch (err) {
    console.error(`Failed to create ${table}:`, err);
    return null;
  }
}

/**
 * Generic update record
 * SECURITY: Filters by user_id (defense-in-depth, RLS also enforces this)
 */
export async function updateRecord<TDb, TEntity>(
  table: TableName,
  id: string,
  updates: Record<string, unknown>,
  mapper: (row: TDb) => TEntity,
  options?: {
    userIdColumn?: string;
    skipUserFilter?: boolean;
  }
): Promise<TEntity | null> {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return null;
  }

  try {
    const userIdColumn = options?.userIdColumn ?? 'user_id';
    let query = supabase!.from(table).update(updates).eq('id', id);

    // Add user filter unless explicitly skipped
    if (!options?.skipUserFilter) {
      const user = await getCurrentUser();
      if (!user) {
        console.warn(`No authenticated user, cannot update ${table}`);
        return null;
      }
      query = query.eq(userIdColumn, user.id);
    }

    const { data, error } = await query.select().single();

    if (error) {
      console.error(`Error updating ${table}:`, error);
      return null;
    }

    return data ? mapper(data as TDb) : null;
  } catch (err) {
    console.error(`Failed to update ${table}:`, err);
    return null;
  }
}

/**
 * Generic delete record
 * SECURITY: Filters by user_id (defense-in-depth, RLS also enforces this)
 */
export async function deleteRecord(
  table: TableName,
  id: string,
  options?: {
    userIdColumn?: string;
    skipUserFilter?: boolean;
  }
): Promise<boolean> {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return false;
  }

  try {
    const userIdColumn = options?.userIdColumn ?? 'user_id';
    let query = supabase!.from(table).delete().eq('id', id);

    // Add user filter unless explicitly skipped
    if (!options?.skipUserFilter) {
      const user = await getCurrentUser();
      if (!user) {
        console.warn(`No authenticated user, cannot delete from ${table}`);
        return false;
      }
      query = query.eq(userIdColumn, user.id);
    }

    const { error } = await query;

    if (error) {
      console.error(`Error deleting from ${table}:`, error);
      return false;
    }

    return true;
  } catch (err) {
    console.error(`Failed to delete from ${table}:`, err);
    return false;
  }
}
